/* eslint-disable max-len */
// src/components/settings/TeamManagement.js
// Team management tab: invite, remove (owner) / view, leave (member)

import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from "firebase/functions";
import { functions } from '../../firebaseConfig';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import { useToast } from '../shared/contexts/ToastContext';
import { FiUsers, FiUserPlus, FiUserMinus, FiMail, FiLoader, FiRefreshCw, FiX } from 'react-icons/fi';

const getTeamInfoFn = httpsCallable(functions, 'getTeamInfo');
const createTeamInviteFn = httpsCallable(functions, 'createTeamInvite');
const removeTeamMemberFn = httpsCallable(functions, 'removeTeamMember');
const leaveTeamFn = httpsCallable(functions, 'leaveTeam');
const revokeTeamInviteFn = httpsCallable(functions, 'revokeTeamInvite');
const resendTeamInviteFn = httpsCallable(functions, 'resendTeamInvite');

function TeamManagement({ userId, userData }) {
  const { confirm } = useConfirmDialog();
  const { addToast } = useToast();

  const [teamInfo, setTeamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // tracks which action is in progress

  const fetchTeamInfo = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const result = await getTeamInfoFn();
      setTeamInfo(result.data);
    } catch (err) {
      addToast('Failed to load team information.', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, addToast]);

  useEffect(() => {
    fetchTeamInfo();
  }, [fetchTeamInfo]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await createTeamInviteFn({ email: inviteEmail.trim() });
      addToast(`Invitation sent to ${inviteEmail.trim()}`, 'success');
      setInviteEmail('');
      fetchTeamInfo();
    } catch (err) {
      const msg = err?.message || 'Failed to send invitation.';
      addToast(msg, 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (member) => {
    const confirmed = await confirm({
      title: 'Remove Team Member',
      message: `Remove ${member.name || member.email} from your team? They will lose access to shared projects.`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!confirmed) return;
    setActionLoading(`remove-${member.userId}`);
    try {
      await removeTeamMemberFn({ memberUserId: member.userId });
      addToast(`${member.name || member.email} has been removed.`, 'success');
      fetchTeamInfo();
    } catch (err) {
      addToast('Failed to remove member.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeaveTeam = async () => {
    const confirmed = await confirm({
      title: 'Leave Team',
      message: 'Are you sure you want to leave this team? You will lose access to all shared projects.',
      confirmLabel: 'Leave Team',
      variant: 'danger',
    });
    if (!confirmed) return;
    setActionLoading('leave');
    try {
      await leaveTeamFn();
      addToast('You have left the team.', 'success');
      fetchTeamInfo();
    } catch (err) {
      addToast('Failed to leave team.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeInvite = async (invite) => {
    setActionLoading(`revoke-${invite.inviteId}`);
    try {
      await revokeTeamInviteFn({ inviteId: invite.inviteId });
      addToast('Invitation revoked.', 'success');
      fetchTeamInfo();
    } catch (err) {
      addToast('Failed to revoke invitation.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvite = async (invite) => {
    setActionLoading(`resend-${invite.inviteId}`);
    try {
      await resendTeamInviteFn({ inviteId: invite.inviteId });
      addToast(`Invitation resent to ${invite.email}`, 'success');
    } catch (err) {
      addToast('Failed to resend invitation.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // --- Loading state ---
  if (loading) {
    return (
      <section className="glass-panel p-10 relative overflow-hidden">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-slate-blue rounded-2xl shadow-glow text-white">
            <FiUsers size={24} />
          </div>
          <h2 className="text-2xl font-black text-cream uppercase tracking-tight">Team</h2>
        </div>
        <div className="text-center py-8">
          <FiLoader className="animate-spin text-2xl text-slate-blue mx-auto mb-3" />
          <p className="text-cream/40 text-sm">Loading team info...</p>
        </div>
      </section>
    );
  }

  // --- No team ---
  // TODO(M5): this becomes the Client Access panel — one login per customer,
  // scoped to their own brands, review/approve only.
  if (!teamInfo?.hasTeam) {
    return (
      <section className="glass-panel p-10 relative overflow-hidden">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-slate-blue rounded-2xl shadow-glow text-white">
            <FiUsers size={24} />
          </div>
          <h2 className="text-2xl font-black text-cream uppercase tracking-tight">Team</h2>
        </div>
        <div className="text-center py-12">
          <FiUsers className="text-5xl text-cream/20 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-cream mb-2">No team yet</h3>
          <p className="text-sm text-cream/40 max-w-md mx-auto">
            Client access is being rebuilt. Clients will get their own login, scoped to their brands.
          </p>
        </div>
      </section>
    );
  }

  const isOwner = teamInfo.role === 'owner';

  return (
    <section className="glass-panel p-10 relative overflow-hidden space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-slate-blue rounded-2xl shadow-glow text-white">
            <FiUsers size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-cream uppercase tracking-tight">Team</h2>
            <p className="text-xs text-cream/30 mt-0.5">{isOwner ? 'Manage your team members' : `Team managed by ${teamInfo.ownerName || teamInfo.ownerEmail}`}</p>
          </div>
        </div>
        <button onClick={fetchTeamInfo} className="text-cream/30 hover:text-cream/60 transition-colors p-2" title="Refresh">
          <FiRefreshCw size={16} />
        </button>
      </div>

      {/* Active members */}
      <div>
        <h3 className="text-xs font-bold text-cream/40 uppercase tracking-wider mb-3">Active Members</h3>
        <div className="space-y-1">
          {(teamInfo.activeMembers || []).map((member) => (
            <div key={member.userId || member.email} className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-slate-blue/20 flex items-center justify-center text-sm font-bold text-slate-blue shrink-0">
                  {(member.name || member.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-cream truncate">{member.name || member.email}</p>
                  <p className="text-xs text-cream/30 truncate">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                  member.role === 'owner'
                    ? 'text-slate-blue bg-slate-blue/10 border border-slate-blue/20'
                    : 'text-cream/40 bg-white/5'
                }`}>
                  {member.role}
                </span>
                {isOwner && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member)}
                    disabled={actionLoading === `remove-${member.userId}`}
                    className="text-soft-coral/60 hover:text-soft-coral transition-colors disabled:opacity-50 p-1"
                    title="Remove member"
                  >
                    {actionLoading === `remove-${member.userId}` ? <FiLoader className="animate-spin" size={14} /> : <FiUserMinus size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending invites (owner only) */}
      {isOwner && teamInfo.pendingInvites?.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-cream/40 uppercase tracking-wider mb-3">Pending Invitations</h3>
          <div className="space-y-1">
            {teamInfo.pendingInvites.map((invite) => {
              const sentDate = invite.createdAt ? new Date(invite.createdAt) : null;
              const daysAgo = sentDate ? Math.floor((Date.now() - sentDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
              return (
                <div key={invite.inviteId} className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-warm-gray/20 flex items-center justify-center text-sm text-cream/30 shrink-0">
                      <FiMail size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-cream/60 truncate">{invite.email}</p>
                      {daysAgo !== null && (
                        <p className="text-xs text-cream/20">Sent {daysAgo === 0 ? 'today' : `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleResendInvite(invite)}
                      disabled={actionLoading === `resend-${invite.inviteId}`}
                      className="text-xs font-medium text-slate-blue/60 hover:text-slate-blue transition-colors disabled:opacity-50 px-2 py-1"
                      title="Resend"
                    >
                      {actionLoading === `resend-${invite.inviteId}` ? <FiLoader className="animate-spin" size={12} /> : <FiRefreshCw size={12} />}
                    </button>
                    <button
                      onClick={() => handleRevokeInvite(invite)}
                      disabled={actionLoading === `revoke-${invite.inviteId}`}
                      className="text-xs font-medium text-soft-coral/60 hover:text-soft-coral transition-colors disabled:opacity-50 px-2 py-1"
                      title="Revoke"
                    >
                      {actionLoading === `revoke-${invite.inviteId}` ? <FiLoader className="animate-spin" size={12} /> : <FiX size={12} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invite form (owner only) */}
      {isOwner && (
        <form onSubmit={handleInvite} className="flex gap-3">
          <div className="flex-1 relative">
            <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/20" size={16} />
            <input
              type="email"
              placeholder="Enter email to invite..."
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={inviting}
              className="w-full bg-black/20 border border-white/5 rounded-xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue transition-all py-3 pl-11 pr-4 text-sm font-medium"
            />
          </div>
          <button
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
            className="btn btn-primary px-6 py-3 rounded-xl shadow-glow flex items-center gap-2 text-sm font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100 shrink-0"
          >
            {inviting ? <FiLoader className="animate-spin" size={14} /> : <FiUserPlus size={14} />}
            Invite
          </button>
        </form>
      )}

      {/* Leave team button (member only) */}
      {!isOwner && (
        <div className="pt-4 border-t border-white/5">
          <button
            onClick={handleLeaveTeam}
            disabled={actionLoading === 'leave'}
            className="inline-flex items-center justify-center py-3 px-6 border border-soft-coral/30 text-sm font-bold text-soft-coral bg-soft-coral/5 hover:bg-soft-coral/10 rounded-full transition-all uppercase tracking-wider disabled:opacity-50"
          >
            {actionLoading === 'leave' ? <FiLoader className="animate-spin mr-2" /> : <FiUserMinus className="mr-2" />}
            Leave Team
          </button>
        </div>
      )}
    </section>
  );
}

export default TeamManagement;
